import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

// Extend dayjs with timezone plugins
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(relativeTime)

/**
 * Convert a UTC date/time string to the user's local timezone
 * @param utcDateTime - ISO string in UTC
 * @param userTimezone - User's timezone (e.g., 'America/Edmonton')
 * @returns dayjs object in the user's timezone
 */
export function convertToUserTimezone(utcDateTime: string, userTimezone: string) {
  return dayjs.utc(utcDateTime).tz(userTimezone)
}

/**
 * Format a time in the user's timezone
 * @param utcDateTime - ISO string in UTC
 * @param userTimezone - User's timezone (e.g., 'America/Edmonton')
 * @param format - Format string (default: 'h:mm A')
 * @returns formatted time string
 */
export function formatTimeInUserTimezone(
  utcDateTime: string,
  userTimezone: string,
  format: string = 'h:mm A'
): string {
  return convertToUserTimezone(utcDateTime, userTimezone).format(format)
}

/**
 * Format a date in the user's timezone
 * @param utcDateTime - ISO string in UTC
 * @param userTimezone - User's timezone (e.g., 'America/Edmonton')
 * @param format - Format string (default: 'ddd, MMM D')
 * @returns formatted date string
 */
export function formatDateInUserTimezone(
  utcDateTime: string,
  userTimezone: string,
  format: string = 'ddd, MMM D'
): string {
  return convertToUserTimezone(utcDateTime, userTimezone).format(format)
}

/**
 * Format a date and time in the user's timezone
 * @param utcDateTime - ISO string in UTC
 * @param userTimezone - User's timezone (e.g., 'America/Edmonton')
 * @param format - Format string (default: 'ddd, MMM D, YYYY h:mm A')
 * @returns formatted date and time string
 */
export function formatDateTimeInUserTimezone(
  utcDateTime: string,
  userTimezone: string,
  format: string = 'ddd, MMM D, YYYY h:mm A'
): string {
  return convertToUserTimezone(utcDateTime, userTimezone).format(format)
}

/**
 * Format a relative time in the user's timezone (e.g., "5 minutes ago")
 * @param utcDateTime - ISO string in UTC
 * @param userTimezone - User's timezone (e.g., 'America/Edmonton')
 * @returns relative time string
 */
export function formatRelativeTimeInUserTimezone(
  utcDateTime: string,
  userTimezone: string
): string {
  return convertToUserTimezone(utcDateTime, userTimezone).fromNow()
}

/**
 * Get today's date string in the user's timezone
 * @param userTimezone - User's timezone (e.g., 'America/Edmonton')
 * @returns date string in YYYY-MM-DD format
 */
export function getTodayInUserTimezone(userTimezone: string): string {
  return dayjs().tz(userTimezone).format('YYYY-MM-DD')
}

/**
 * Get tomorrow's date string in the user's timezone
 * @param userTimezone - User's timezone (e.g., 'America/Edmonton')
 * @returns date string in YYYY-MM-DD format
 */
export function getTomorrowInUserTimezone(userTimezone: string): string {
  return dayjs().tz(userTimezone).add(1, 'day').format('YYYY-MM-DD')
}

/**
 * Check if a UTC date matches today in the user's timezone
 * @param utcDateTime - ISO string in UTC
 * @param userTimezone - User's timezone (e.g., 'America/Edmonton')
 * @returns boolean
 */
export function isToday(utcDateTime: string, userTimezone: string): boolean {
  const userDate = convertToUserTimezone(utcDateTime, userTimezone)
  const today = dayjs().tz(userTimezone)
  return userDate.isSame(today, 'day')
}

/**
 * Check if a UTC date matches yesterday in the user's timezone
 * @param utcDateTime - ISO string in UTC
 * @param userTimezone - User's timezone (e.g., 'America/Edmonton')
 * @returns boolean
 */
export function isYesterday(utcDateTime: string, userTimezone: string): boolean {
  const userDate = convertToUserTimezone(utcDateTime, userTimezone)
  const yesterday = dayjs().tz(userTimezone).subtract(1, 'day')
  return userDate.isSame(yesterday, 'day')
}

/**
 * Check if a UTC date is within the current week in the user's timezone
 * @param utcDateTime - ISO string in UTC
 * @param userTimezone - User's timezone (e.g., 'America/Edmonton')
 * @returns boolean
 */
export function isThisWeek(utcDateTime: string, userTimezone: string): boolean {
  const userDate = convertToUserTimezone(utcDateTime, userTimezone)
  const today = dayjs().tz(userTimezone)
  return userDate.isSame(today, 'week')
}

/**
 * Format business hours (HH:MM strings or UTC ISO strings) in user's timezone
 * @param openTime - Opening time string (e.g., "09:00" or "2024-01-01T16:00:00.000Z")
 * @param closeTime - Closing time string (e.g., "17:00" or "2024-01-01T24:00:00.000Z")
 * @param userTimezone - User's timezone
 * @param referenceDate - Optional date for DST handling (defaults to today)
 * @returns Formatted business hours object
 */
export function formatBusinessHoursInUserTimezone(
  openTime: string | null,
  closeTime: string | null,
  userTimezone: string,
  referenceDate?: string
): { open: string; close: string; displayText: string } {
  try {
    // Handle null values
    if (!openTime || !closeTime) {
      return {
        open: 'Closed',
        close: 'Closed',
        displayText: 'Closed'
      }
    }

    // Check if times are already in UTC ISO format
    const isUTCFormat = (time: string) => time.includes('T') && time.includes('Z')

    if (isUTCFormat(openTime) && isUTCFormat(closeTime)) {
      // Handle UTC ISO strings - convert directly to user timezone
      const openDateTime = convertToUserTimezone(openTime, userTimezone)
      const closeDateTime = convertToUserTimezone(closeTime, userTimezone)

      const formattedOpen = openDateTime.format('h:mm A')
      const formattedClose = closeDateTime.format('h:mm A')

      return {
        open: formattedOpen,
        close: formattedClose,
        displayText: `${formattedOpen} - ${formattedClose}`
      }
    } else {
      // Handle legacy HH:MM format - assume local business timezone
      // Use provided reference date or today's date in user timezone
      const refDate = referenceDate
        ? dayjs(referenceDate).tz(userTimezone)
        : dayjs().tz(userTimezone)

      // Create full datetime objects for the business hours using the reference date
      const openDateTime = refDate.hour(parseInt(openTime.split(':')[0])).minute(parseInt(openTime.split(':')[1]))
      const closeDateTime = refDate.hour(parseInt(closeTime.split(':')[0])).minute(parseInt(closeTime.split(':')[1]))

      // Format the times in user timezone
      const formattedOpen = openDateTime.format('h:mm A')
      const formattedClose = closeDateTime.format('h:mm A')

      return {
        open: formattedOpen,
        close: formattedClose,
        displayText: `${formattedOpen} - ${formattedClose}`
      }
    }
  } catch (error) {
    console.error('Error formatting business hours:', error)
    // Fallback to original times if conversion fails, handle null values
    return {
      open: openTime || 'Closed',
      close: closeTime || 'Closed',
      displayText: openTime && closeTime ? `${openTime} - ${closeTime}` : 'Closed'
    }
  }
}

/**
 * Convert a full business hours object to user timezone
 * @param businessHours - Object with day keys and open/close times
 * @param userTimezone - User's timezone
 * @returns Converted business hours object
 */
export function convertBusinessHoursToUserTimezone(
  businessHours: Record<string, { open: string | null; close: string | null; isClosed?: boolean }>,
  userTimezone: string
): Record<string, { open: string; close: string; isClosed?: boolean; displayText?: string }> {
  const convertedHours: Record<string, { open: string; close: string; isClosed?: boolean; displayText?: string }> = {}

  Object.entries(businessHours).forEach(([day, hours]) => {
    if (hours.isClosed || !hours.open || !hours.close) {
      convertedHours[day] = {
        open: 'Closed',
        close: 'Closed',
        isClosed: true,
        displayText: 'Closed'
      }
    } else {
      const formatted = formatBusinessHoursInUserTimezone(hours.open, hours.close, userTimezone)
      convertedHours[day] = {
        open: formatted.open,
        close: formatted.close,
        isClosed: hours.isClosed,
        displayText: formatted.displayText
      }
    }
  })

  return convertedHours
}

/**
 * Format a Date object in the user's timezone (for scheduling components)
 * @param date - Date object
 * @param userTimezone - User's timezone (e.g., 'America/Edmonton')
 * @param format - Format string (default: 'h:mm A')
 * @returns formatted time string
 */
export function formatDateObjectInUserTimezone(
  date: Date,
  userTimezone: string,
  format: string = 'h:mm A'
): string {
  return dayjs(date).tz(userTimezone).format(format)
}

/**
 * Format a date string or Date object in the user's timezone (unified function)
 * @param dateInput - ISO string, Date object, or date string
 * @param userTimezone - User's timezone (e.g., 'America/Edmonton')
 * @param format - Format string (default: 'h:mm A')
 * @returns formatted time string
 */
export function formatAnyDateInUserTimezone(
  dateInput: string | Date,
  userTimezone: string,
  format: string = 'h:mm A'
): string {
  if (dateInput instanceof Date) {
    return formatDateObjectInUserTimezone(dateInput, userTimezone, format)
  } else {
    return formatTimeInUserTimezone(dateInput, userTimezone, format)
  }
}

/**
 * Get the current time in the user's timezone
 * @param userTimezone - User's timezone (e.g., 'America/Edmonton')
 * @returns dayjs object representing current time in user's timezone
 */
export function getCurrentTimeInUserTimezone(userTimezone: string) {
  return dayjs().tz(userTimezone)
}

/**
 * Get timezone abbreviation for a given timezone
 * @param userTimezone - User's timezone (e.g., 'America/Edmonton')
 * @param date - Optional date to check (defaults to now)
 * @returns timezone abbreviation (e.g., 'MST', 'MDT')
 */
export function getTimezoneAbbreviation(userTimezone: string, date?: string): string {
  const targetDate = date ? dayjs(date).tz(userTimezone) : dayjs().tz(userTimezone)
  return targetDate.format('z')
}

/**
 * Convert a time from one timezone to another
 * @param dateTime - ISO string or Date object
 * @param fromTimezone - Source timezone
 * @param toTimezone - Target timezone
 * @returns ISO string in target timezone
 */
export function convertBetweenTimezones(
  dateTime: string | Date,
  fromTimezone: string,
  toTimezone: string
): string {
  const date = typeof dateTime === 'string' ? dayjs(dateTime) : dayjs(dateTime)
  return date.tz(fromTimezone).tz(toTimezone).toISOString()
}

/**
 * Get a smart relative time format that shows appropriate detail
 * @param utcDateTime - ISO string in UTC
 * @param userTimezone - User's timezone
 * @returns Smart formatted string
 */
export function getSmartRelativeTime(utcDateTime: string, userTimezone: string): string {
  const userDate = convertToUserTimezone(utcDateTime, userTimezone)
  const now = dayjs().tz(userTimezone)

  const diffMinutes = now.diff(userDate, 'minute')
  const diffHours = now.diff(userDate, 'hour')
  const diffDays = now.diff(userDate, 'day')

  // Just now (< 1 minute)
  if (diffMinutes < 1) {
    return 'just now'
  }

  // Minutes ago (< 1 hour)
  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`
  }

  // Hours ago (< 24 hours)
  if (diffHours < 24) {
    return `${diffHours}h ago`
  }

  // Days ago (< 7 days)
  if (diffDays < 7) {
    return `${diffDays}d ago`
  }

  // This week
  if (isThisWeek(utcDateTime, userTimezone)) {
    return userDate.format('dddd')
  }

  // This year
  if (userDate.year() === now.year()) {
    return userDate.format('MMM D')
  }

  // Previous years
  return userDate.format('MMM D, YYYY')
}

/**
 * Validate if a timezone string is valid
 * @param timezone - Timezone string to validate
 * @returns boolean indicating if timezone is valid
 */
export function isValidTimezone(timezone: string): boolean {
  try {
    dayjs().tz(timezone)
    return true
  } catch {
    return false
  }
}

/**
 * Get user's timezone from various sources with fallback
 * @param sessionTimezone - Timezone from user session
 * @param browserTimezone - Timezone detected from browser
 * @param defaultTimezone - Default fallback timezone
 * @returns Valid timezone string
 */
export function getUserTimezone(
  sessionTimezone?: string,
  browserTimezone?: string,
  defaultTimezone: string = 'America/Edmonton'
): string {
  // Priority: session > browser > default
  const candidates = [sessionTimezone, browserTimezone, defaultTimezone].filter(Boolean)

  for (const timezone of candidates) {
    if (timezone && isValidTimezone(timezone)) {
      return timezone
    }
  }

  return defaultTimezone
}

/**
 * Convert an HH:MM string and a day of week to a UTC ISO string for the next occurrence of that day
 * @param hhmm - Time in HH:MM format
 * @param dayOfWeek - Day of week (e.g., 'monday')
 * @param timezoneStr - Timezone string (default: 'America/Edmonton')
 * @returns UTC ISO string
 */
export function toUTCISOFromHHMM(hhmm: string, dayOfWeek: string, timezoneStr: string = 'America/Edmonton'): string {
  // Find the next occurrence of the given day
  const daysOfWeek = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday']
  const today = dayjs().tz(timezoneStr)
  const targetDayIdx = daysOfWeek.indexOf(dayOfWeek.toLowerCase())
  if (targetDayIdx === -1) throw new Error('Invalid day of week')
  const todayIdx = today.day()
  let daysToAdd = (targetDayIdx - todayIdx + 7) % 7
  // If today is the target day, use today
  if (daysToAdd === 0) daysToAdd = 0
  const [hour, minute] = hhmm.split(':').map(Number)
  const localDate = today.add(daysToAdd, 'day').hour(hour).minute(minute).second(0).millisecond(0)
  return localDate.tz('UTC').toISOString()
}

/**
 * Convert a UTC ISO string to HH:MM in the user's timezone.
 */
export function utcIsoToHHMM(iso: string | null, userTimezone: string = 'America/Edmonton'): string {
  if (!iso) return ''
  if (iso.includes('T') && iso.includes('Z')) {
    return dayjs(iso).tz(userTimezone).format('HH:mm')
  }
  // fallback for legacy HH:MM
  return iso
}

/**
 * Canonical day keys for the capacity allocator's openingHours JSON shape.
 * The shape stored on `ShopLocation.openingHours` is:
 *   { mon: "08:00-18:00", tue: "08:00-18:00", ..., sun: null }
 * Keys are 3-letter lowercase, values are "HH:MM-HH:MM" UTC strings or null.
 */
const SHAPE_A_DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const
type ShapeADay = (typeof SHAPE_A_DAYS)[number]

const FULL_DAY_TO_SHORT: Record<string, ShapeADay> = {
  sunday: 'sun',
  monday: 'mon',
  tuesday: 'tue',
  wednesday: 'wed',
  thursday: 'thu',
  friday: 'fri',
  saturday: 'sat'
}

export type OpeningHoursShapeA = Record<ShapeADay, string | null>

/**
 * Convert local-time opening hours into the canonical Shape A used by the
 * capacity allocator (`src/lib/services/capacity/opening-hours.ts`):
 *
 *   { mon: "HH:MM-HH:MM", sun: null, ... }
 *
 * - Day keys are normalized to 3-letter lowercase (`sun mon tue wed thu fri sat`)
 * - Times are converted from the location-local `HH:MM` to UTC `HH:MM`
 *   using the next occurrence of each day for DST stability
 * - Closed days (null open/close) collapse to a single `null`
 *
 * Accepts the bootstrap-seeder input shape:
 *   { monday: { open: "08:00", close: "17:00" }, sunday: { open: null, close: null }, ... }
 *
 * @param openingHours - Opening hours with full-day-name keys + open/close HH:MM (local)
 * @param timezone - Location timezone (e.g., 'America/Edmonton')
 * @returns Shape A opening hours: { mon: "HH:MM-HH:MM" | null, ... }
 */
export function convertOpeningHoursToShapeA(
  openingHours:
    | Record<string, { open: string | null; close: string | null }>
    | OpeningHoursShapeA,
  timezone: string
): OpeningHoursShapeA {
  // Initialize all 7 days to null (closed) so the result is always complete
  const result: OpeningHoursShapeA = {
    sun: null,
    mon: null,
    tue: null,
    wed: null,
    thu: null,
    fri: null,
    sat: null
  }

  // Idempotent on Shape A input. Treat 3-letter day keys with string|null
  // values as already-canonical UTC and pass through. This lets bootstrap
  // JSONs sync'd from prod (which stores Shape A) round-trip without the
  // converter shifting their times by the timezone offset.
  const inputKeys = Object.keys(openingHours)
  const looksLikeShapeA =
    inputKeys.length > 0 &&
    inputKeys.every((k) => (SHAPE_A_DAYS as readonly string[]).includes(k.toLowerCase())) &&
    inputKeys.every((k) => {
      const v = (openingHours as Record<string, unknown>)[k]
      return v === null || typeof v === 'string'
    })
  if (looksLikeShapeA) {
    for (const k of inputKeys) {
      const day = k.toLowerCase() as ShapeADay
      const v = (openingHours as Record<string, string | null>)[k]
      result[day] = v ?? null
    }
    return result
  }

  Object.entries(
    openingHours as Record<string, { open: string | null; close: string | null }>
  ).forEach(([rawDay, hours]) => {
    const shortDay = FULL_DAY_TO_SHORT[rawDay.toLowerCase()] ?? null
    if (!shortDay) {
      // Unknown day key — skip rather than crash
      return
    }

    if (!hours || !hours.open || !hours.close) {
      result[shortDay] = null
      return
    }

    try {
      const openHHMM = localHHMMToUTCHHMM(hours.open, rawDay, timezone)
      const closeHHMM = localHHMMToUTCHHMM(hours.close, rawDay, timezone)
      result[shortDay] = `${openHHMM}-${closeHHMM}`
    } catch (error) {
      console.error(`Error converting opening hours for ${rawDay}:`, error)
      // Mark day as closed on conversion failure rather than emitting bad data
      result[shortDay] = null
    }
  })

  return result
}

/**
 * Convert a local HH:MM time on a given day-of-week to a UTC HH:MM string.
 * Uses the next occurrence of that day so daylight-savings is applied correctly
 * for the period in which the seed will actually be used.
 */
function localHHMMToUTCHHMM(hhmm: string, dayOfWeek: string, timezoneStr: string): string {
  const iso = toUTCISOFromHHMM(hhmm, dayOfWeek, timezoneStr)
  return dayjs.utc(iso).format('HH:mm')
}

/**
 * Convert opening hours from local time (HH:MM) to UTC ISO strings for database storage
 * @param openingHours - Opening hours object with HH:MM strings in local time
 * @param timezone - Location timezone (e.g., 'America/Edmonton')
 * @returns Opening hours with UTC ISO strings
 */
export function convertOpeningHoursToUTC(
  openingHours: Record<string, { open: string | null; close: string | null }>,
  timezone: string
): Record<string, { open: string | null; close: string | null }> {
  const convertedHours: Record<string, { open: string | null; close: string | null }> = {}

  Object.entries(openingHours).forEach(([day, hours]) => {
    if (!hours.open || !hours.close) {
      // Handle closed days
      convertedHours[day] = { open: null, close: null }
    } else {
      try {
        // Convert local HH:MM time to UTC ISO string using next occurrence of this day
        convertedHours[day] = {
          open: toUTCISOFromHHMM(hours.open, day, timezone),
          close: toUTCISOFromHHMM(hours.close, day, timezone)
        }
      } catch (error) {
        console.error(`Error converting opening hours for ${day}:`, error)
        // Fallback to original values
        convertedHours[day] = { open: hours.open, close: hours.close }
      }
    }
  })

  return convertedHours
}

/**
 * Convert special hours from local time (HH:MM) to UTC ISO strings for database storage
 * @param specialHours - Special hours object with date keys and HH:MM strings in local time
 * @param timezone - Location timezone (e.g., 'America/Edmonton')
 * @returns Special hours with UTC ISO strings
 */
export function convertSpecialHoursToUTC(
  specialHours: Record<string, { open: string | null; close: string | null }>,
  timezone: string
): Record<string, { open: string | null; close: string | null }> {
  const convertedSpecialHours: Record<string, { open: string | null; close: string | null }> = {}

  Object.entries(specialHours).forEach(([dateString, hours]) => {
    if (!hours.open || !hours.close) {
      // Handle closed days
      convertedSpecialHours[dateString] = { open: null, close: null }
    } else {
      try {
        // Parse the date and create full datetime objects in the location timezone
        const [openHour, openMinute] = hours.open.split(':').map(Number)
        const [closeHour, closeMinute] = hours.close.split(':').map(Number)

        // Create datetime objects for the specific date in the location timezone
        const openDateTime = dayjs(dateString).tz(timezone).hour(openHour).minute(openMinute).second(0).millisecond(0)
        const closeDateTime = dayjs(dateString).tz(timezone).hour(closeHour).minute(closeMinute).second(0).millisecond(0)

        convertedSpecialHours[dateString] = {
          open: openDateTime.utc().toISOString(),
          close: closeDateTime.utc().toISOString()
        }
      } catch (error) {
        console.error(`Error converting special hours for ${dateString}:`, error)
        // Fallback to original values
        convertedSpecialHours[dateString] = { open: hours.open, close: hours.close }
      }
    }
  })

  return convertedSpecialHours
}

/**
 * Convert staff preferred hours from local time (HH:MM) to UTC ISO strings for database storage
 * @param preferredHours - Preferred hours object with day keys and HH:MM strings in local time
 * @param timezone - Location timezone (e.g., 'America/Edmonton')
 * @returns Preferred hours with UTC ISO strings
 */
export function convertStaffPreferredHoursToUTC(
  preferredHours: Record<string, { start: string | null; end: string | null }>,
  timezone: string
): Record<string, { start: string | null; end: string | null }> {
  const convertedHours: Record<string, { start: string | null; end: string | null }> = {}

  Object.entries(preferredHours).forEach(([day, hours]) => {
    if (!hours.start || !hours.end) {
      // Handle unavailable days
      convertedHours[day] = { start: null, end: null }
    } else {
      try {
        // Convert local HH:MM time to UTC ISO string using next occurrence of this day
        convertedHours[day] = {
          start: toUTCISOFromHHMM(hours.start, day, timezone),
          end: toUTCISOFromHHMM(hours.end, day, timezone)
        }
      } catch (error) {
        console.error(`Error converting preferred hours for ${day}:`, error)
        // Fallback to original values
        convertedHours[day] = { start: hours.start, end: hours.end }
      }
    }
  })

  return convertedHours
}

/**
 * Convert a shift template time from local time (HH:MM) to UTC ISO string
 * @param timeString - Time in HH:MM format
 * @param dayOfWeek - Day of week (e.g., 'monday')
 * @param timezone - Location timezone (e.g., 'America/Edmonton')
 * @returns UTC ISO string for shift template
 */
export function convertShiftTimeToUTC(
  timeString: string,
  dayOfWeek: string,
  timezone: string
): string {
  return toUTCISOFromHHMM(timeString, dayOfWeek, timezone)
}
